import type { CSSProperties, ReactNode } from 'react'
import type { WidgetDisplay } from './schemas'
import { STYLE_PRESETS, ICON_MAP } from './config/stylePresets'

interface WidgetShellProps {
  display?: WidgetDisplay
  /** Classes propres au widget (padding, hauteur, etc. ex. 'p-5 h-full'). */
  className?: string
  children: ReactNode
}

/**
 * Cadre stylable PARTAGÉ appliqué autour de chaque widget du builder.
 * Piloté par `config.display.*` (fond / bordure / ombre / rayon / icône / badge).
 * Les défauts de preset sont résolus via `STYLE_PRESETS` (source unique, DRY).
 */
export default function WidgetShell({ display, className, children }: WidgetShellProps) {
  const d = display ?? {}
  const { preset, bg, border, border_color, icon, icon_color, shadow, badge } = d

  // Cadre par défaut Orion : aucun style ni override (plain implicite ou explicite).
  const isPlainDefault = (!preset || preset === 'plain') && !bg && !border && !icon && !badge

  if (isPlainDefault) {
    return (
      <div className={`rounded-xl border border-[#E2E4ED] bg-white ${className ?? ''}`}>
        {children}
      </div>
    )
  }

  // Défauts du preset (source unique STYLE_PRESETS), overrides explicites prioritaires.
  const presetDefaults = preset ? STYLE_PRESETS.find((p) => p.key === preset) : undefined
  const finalBg = bg ?? presetDefaults?.bg ?? 'transparent'
  // Un preset pastel implique une bordure 1px assortie, même si `display.border`
  // n'a pas été persisté (config partielle).
  const finalBorder = border ?? (presetDefaults?.border_color ? 'full' : 'none')
  const finalBorderColor = border_color ?? presetDefaults?.border_color ?? '#e5e7eb'
  // Aucune icône imposée par un preset : `display.icon` (réglage manuel) uniquement.
  const finalIcon = icon
  const finalIconColor = icon_color ?? finalBorderColor
  const finalShadow = shadow ?? false
  // Chip éditorial du badge : teinté par le preset, sinon neutre.
  const chipBg = presetDefaults?.chip_bg ?? '#EEF0F5'
  const chipText = presetDefaults?.chip_text ?? '#6B7280'

  const IconComponent = finalIcon ? ICON_MAP[finalIcon] : null

  // Longhand uniquement pour éviter le conflit shorthand/longhand React.
  const containerStyle: CSSProperties = {
    backgroundColor: finalBg === 'transparent' ? undefined : finalBg,
    borderLeft:
      finalBorder === 'left'
        ? `4px solid ${finalBorderColor}`
        : finalBorder === 'full'
          ? `1px solid ${finalBorderColor}`
          : undefined,
    borderRight: finalBorder === 'full' ? `1px solid ${finalBorderColor}` : undefined,
    borderTop:
      finalBorder === 'top'
        ? `4px solid ${finalBorderColor}`
        : finalBorder === 'full'
          ? `1px solid ${finalBorderColor}`
          : undefined,
    borderBottom: finalBorder === 'full' ? `1px solid ${finalBorderColor}` : undefined,
    boxShadow: finalShadow
      ? '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)'
      : undefined,
  }

  return (
    <div className={`rounded-xl ${className ?? ''}`} style={containerStyle}>
      {badge && (
        <div className="mb-2">
          <span
            className="inline-block text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: chipBg, color: chipText }}
          >
            {badge}
          </span>
        </div>
      )}

      <div className={IconComponent ? 'flex gap-3 h-full' : 'h-full'}>
        {IconComponent && (
          <div className="shrink-0 mt-0.5">
            <IconComponent size={20} style={{ color: finalIconColor }} />
          </div>
        )}
        <div className="flex-1 min-w-0 h-full">{children}</div>
      </div>
    </div>
  )
}
